/**
 * KA-49 §3 — BIỂU ĐỒ MIỀN NHIỀU LỚP: phần THUẦN (không React, không recharts) để bộ kiểm nhập thẳng.
 *
 * 🔴 LUẬT SỐ 0 cho biểu đồ: ngày/khoảng THIẾU SỐ là `null` và phải đi tới recharts NGUYÊN là `null`
 *   (miền ngắt ở đó, `connectNulls={false}`) — không bao giờ `?? 0`. Một miền tụt xuống 0 là một lời khai "đêm ấy
 *   không tốn điện", trong khi sự thật là "đêm ấy không đo được".
 *   Chỗ thiếu số còn được tô một DẢI GẠCH CHÉO (`khoangThieu`) để mắt thấy là thiếu, không phải thấp.
 */

/** Màu mỗi lớp — theo thứ tự mẫu lee duyệt: cyan · xanh ngọc · tím. Chỉ trang trí, KHÔNG mang nghĩa trạng thái. */
export const MAU_LOP = ['--nhan', '--xanh-ngoc', '--tim', '--nhan-dam', '--anh'] as const;

/**
 * Chuẩn hoá chuỗi cho biểu đồ: mỗi khoá trong `khoa` giữ đúng số hoặc `null` (undefined / NaN ⇒ null). KHÔNG đổi null thành 0.
 */
export function duLieuMien<T extends Record<string, unknown>>(ds: readonly T[], khoa: readonly string[]): (T & Record<string, number | null>)[] {
  return ds.map((d) => {
    const r: Record<string, unknown> = { ...d };
    for (const k of khoa) {
      const v = d[k];
      r[k] = typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    return r as T & Record<string, number | null>;
  });
}

/**
 * Các khoảng liên tiếp mà MỌI khoá đều null (không lớp nào có số) — trả về cặp [nhãn đầu, nhãn cuối] theo trục `truc`.
 * Dùng để vẽ dải gạch chéo "thiếu số". Một điểm thiếu lẻ ⇒ khoảng một điểm (đầu = cuối).
 */
export interface KhoangThieu { dau: unknown; cuoi: unknown; so: number; i0: number; i1: number; x1: unknown; x2: unknown }
export function khoangThieu<T extends Record<string, unknown>>(ds: readonly T[], khoa: readonly string[], truc: string): KhoangThieu[] {
  const ra: KhoangThieu[] = [];
  let i0 = -1;
  const dong = (i1: number) => {
    /* dải trải từ điểm CÓ SỐ trước tới điểm CÓ SỐ sau (mép miền bị ngắt), không chỉ đúng ô thiếu — đúng hình mẫu */
    ra.push({ dau: ds[i0][truc], cuoi: ds[i1][truc], so: i1 - i0 + 1, i0, i1, x1: ds[Math.max(0, i0 - 1)][truc], x2: ds[Math.min(ds.length - 1, i1 + 1)][truc] });
    i0 = -1;
  };
  ds.forEach((d, i) => {
    const thieu = khoa.every((k) => typeof d[k] !== 'number' || !Number.isFinite(d[k] as number));
    if (thieu && i0 < 0) i0 = i;
    if (!thieu && i0 >= 0) dong(i - 1);
  });
  if (i0 >= 0) dong(ds.length - 1);
  return ra;
}

/** Trung bình trượt `n` điểm, bỏ qua null; cửa sổ không có số nào ⇒ null (không 0). */
export function trungBinhTruot(ds: readonly (number | null)[], n: number): (number | null)[] {
  return ds.map((_, i) => {
    const cua = ds.slice(Math.max(0, i - n + 1), i + 1).filter((v): v is number => v !== null);
    return cua.length ? cua.reduce((t, v) => t + v, 0) / cua.length : null;
  });
}

/**
 * Điểm LẺ của một lớp: có số mà hai bên đều không có (hoặc là mép). Miền một điểm không có bề rộng ⇒ recharts không vẽ gì —
 * một ngày CÓ số mà không hiện là lỗi ngược chiều luật số 0 (có số mà trông như thiếu). Điểm lẻ được vẽ một CHẤM.
 */
export function diemLe(ds: readonly Record<string, unknown>[], khoa: string): Set<number> {
  const co = (i: number) => i >= 0 && i < ds.length && typeof ds[i][khoa] === 'number' && Number.isFinite(ds[i][khoa] as number);
  const ra = new Set<number>();
  ds.forEach((_, i) => { if (co(i) && !co(i - 1) && !co(i + 1)) ra.add(i); });
  return ra;
}
